import confetti from "canvas-confetti";
import React from "react";

// components
import classNames from "classnames";

// styles
import { Button } from "@clockwise/design-system";
import { Edit, InfoOutlined, Link, Reply } from "@clockwise/design-system/icons";
import { animated, config, useSpring } from "@react-spring/web";

// util
import { AvailabilityRestrictionEnum } from "@clockwise/schema/v2";
import { getRandomIntInRange } from "../../util/browser.util";
import { formatDuration } from "../../util/duration.util";
import { UserAvatar } from "../UserAvatar";
import { AvatarStack } from "../avatar-stack/AvatarStack";
import { AVAILABILITY_TEXT_SHORT } from "../scheduling-links/constants";

const AnimatedButton = animated(Button);

export interface SchedulingLinkCardProps {
  name: string;
  availabilityType: AvailabilityRestrictionEnum;
  durations: number[];
  defaultDuration: number;
  active: boolean;
  linkMembers: Array<{
    calendarId: string;
    userProfile: {
      givenName: string | null;
      familyName: string | null;
      externalImageUrl: string | null;
    };
  }>;
  onCopyLink: () => Promise<void>;
  onEditLink?: () => Promise<void>;
  onCopyTimes: () => Promise<void>;
  onOpen: () => Promise<void>;
  className?: string;
}

/**
 * @deprecated Use packages/web-commons/src/ui/scheduling-link-card/SchedulingLinkCard.tsx instead
 */
export const SchedulingLinkCard = ({
  name,
  availabilityType,
  active,
  durations,
  defaultDuration,
  linkMembers,
  onCopyLink,
  onEditLink,
  onCopyTimes,
  onOpen,
  className,
}: SchedulingLinkCardProps) => {
  // refs
  const confettiCanvasRef = React.useRef<HTMLCanvasElement>(null);
  const confettiRef = React.useRef<confetti.CreateTypes | null>(null);

  const [copied, setCopied] = React.useState(false);
  const formattedDuration =
    durations.length > 1 ? `${durations.length} duration options` : formatDuration(defaultDuration);

  const { contentOpacity, copiedMsgOpacity } = useSpring({
    contentOpacity: copied ? 0 : 1,
    copiedMsgOpacity: copied ? 1 : 0,
    config: { mass: 1, tension: 200, friction: 23 },
  });

  const { textScale } = useSpring({
    textScale: copied ? 0 : 1,
    immediate: !copied, // Prevents a second animation when copied is returned to false.
    config: { mass: 1, tension: 170, friction: 18, precision: 0.02 },
  });

  const scaleInterpolateRange = [0, 0.25, 0.35, 0.45, 0.5, 0.55, 0.65, 0.75, 1];
  const scaleInterpolateOutput = [1.04, 1.06, 1.07, 1.08, 1.1, 1.09, 1.08, 1.06, 1];

  React.useEffect(() => {
    if (copied && confettiCanvasRef.current) {
      if (!confettiRef.current) {
        confettiRef.current = confetti.create(confettiCanvasRef.current, {
          useWorker: false, // Cannot use web workers with multiple canvases.
          // Confetti would only pop on a single scheduling-link-card otherwise.
          resize: true,
        });
      }

      const randomColors = ["#FFFFFF", "#FFDE34"]; // #FFDE34 is emoji yellow
      randomColors.splice(getRandomIntInRange(0, 1), getRandomIntInRange(0, 1));

      // Put into an array, so we can add lots of random confettis
      const confettis = [
        {
          // Left to right, orginating from near the copy button
          particleCount: getRandomIntInRange(80, 120),
          spread: getRandomIntInRange(50, 150),
          startVelocity: getRandomIntInRange(18, 26),
          ticks: getRandomIntInRange(280, 320),
          drift: getRandomIntInRange(1, 6) / 10,
          origin: { x: 0.2, y: 1.2 }, // Sets it to pop from the copy button.
          angle: getRandomIntInRange(52, 70),
          colors: randomColors,
          scalar: 0.2,
          disableForReducedMotion: true,
        },
      ];

      void confettiRef.current(confettis[Math.floor(Math.random() * confettis.length)]);
    }
  }, [copied]);

  const [hovering, setHovering] = React.useState(false);
  const [{ scale }] = useSpring(
    {
      scale: hovering ? 1.035 : 1.0,
      config: { ...config.stiff },
    },
    [hovering],
  );

  const handleCopyLink = React.useCallback(async () => {
    await onCopyLink?.();
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
      if (confettiRef.current) {
        confettiRef.current.reset?.();
      }
    }, 2100);
  }, [onCopyLink]);

  return (
    <animated.div
      style={{
        transform: textScale
          .to({
            range: scaleInterpolateRange,
            output: scaleInterpolateOutput.map((s) => s - (s - 1) * 0.7), // Scale, but not as much.
          })
          .to((x) => `scale(${x})`),
        scale,
      }}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      onClick={() => {
        if (active) {
          void onOpen();
          return;
        }
      }}
      cw-id="links-sidebar-card"
      className={classNames(
        "cw-border cw-border-solid cw-border-default cw-rounded-lg cw-shadow-md",
        "cw-relative cw-px-4 cw-py-2 cw-max-w-[425px]",
        className,
        {
          "cw-bg-default-hover": !active,
          "cw-bg-default cw-cursor-pointer": active,
        },
      )}
      title={active ? "Preview Link" : "Inactive Link"}
    >
      <animated.div className="cw-flex cw-flex-col cw-gap-1" style={{ opacity: contentOpacity }}>
        <div className="cw-flex cw-flex-col cw-gap-1 cw-pt-1">
          <h2 className="cw-heading-sm cw-m-0" cw-id="links-card-meeting-title">
            {name}
          </h2>
          {linkMembers.length > 1 ? ( // Display in an AvatarStack of UserAvatars
            <>
              <AvatarStack maxShown={8} size="small" overlap>
                {linkMembers.map((linkMember) => (
                  <UserAvatar
                    key={linkMember.calendarId}
                    profile={linkMember.userProfile}
                    size="small"
                  />
                ))}
              </AvatarStack>
              <div className="cw-text-subtle cw-leading-tight cw-font-body cw-text-sm">
                {formattedDuration}
              </div>
            </>
          ) : (
            <div className="cw-font-body cw-text-sm cw-text-subtle">
              {formattedDuration} • {AVAILABILITY_TEXT_SHORT[availabilityType]}
            </div>
          )}
        </div>
        <div className="-cw-ml-[10px]">
          <div className="cw-flex cw-flex-row cw-justify-around cw-flex-nowrap cw-overflow-hidden cw-gap-2">
            {active ? (
              <>
                <AnimatedButton
                  onClick={(e: React.MouseEvent<HTMLElement>) => {
                    e.stopPropagation();
                    e.preventDefault();
                    void onCopyTimes();
                  }}
                  variant="text"
                  size="small"
                  startIcon={Reply}
                  sentiment="positive"
                  cw-id="links-card-copy-times-button"
                >
                  Share
                </AnimatedButton>
                <Button
                  onClick={(e: React.MouseEvent<HTMLElement>) => {
                    e.stopPropagation();
                    e.preventDefault();
                    void handleCopyLink();
                  }}
                  variant="text"
                  size="small"
                  sentiment="neutral"
                  startIcon={Link}
                  cw-id="links-card-copy-link-button"
                >
                  Copy link
                </Button>
              </>
            ) : (
              <div className="cw-text-default cw-h-8 cw-text-12 cw-px-2 cw-leading-none cw-font-body cw-font-bold cw-whitespace-nowrap cw-flex cw-items-center cw-justify-center cw-gap-1">
                <InfoOutlined className="cw-w-4" />
                Inactive Link
              </div>
            )}
            <div>
              <Button
                onClick={(e: React.MouseEvent<HTMLElement>) => {
                  e.stopPropagation();
                  e.preventDefault();
                  void onEditLink?.();
                }}
                variant="text"
                size="small"
                sentiment="neutral"
                startIcon={Edit}
                cw-id="links-card-edit-link-button"
              >
                Edit
              </Button>
            </div>
          </div>
        </div>
      </animated.div>
      <animated.div
        className="cw-absolute cw-inset-0 cw-flex cw-items-center cw-justify-center cw-bg-brand-emphasis cw-rounded-lg"
        style={{ opacity: copiedMsgOpacity, visibility: copied ? "visible" : "hidden" }}
      >
        <canvas ref={confettiCanvasRef} className="cw-absolute cw-w-full cw-h-full" />
        <animated.div
          className="cw-body-base cw-text-onEmphasis"
          style={{
            transform: textScale
              .to({
                range: scaleInterpolateRange,
                output: scaleInterpolateOutput,
              })
              .to((x) => `scale(${x})`),
          }}
        >
          Link copied to your clipboard 🙌
        </animated.div>
      </animated.div>
    </animated.div>
  );
};
